/**
 * Badge System
 * Manages achievements/badges and their unlock conditions
 */

const BadgeSystem = {
    // Badge definitions
    badges: {
        firstVictory: {
            id: 'firstVictory',
            name: 'Primera Victoria',
            description: 'Completaste tu primer problema',
            icon: '🏆',
            condition: (progress) => progress.problemsCompleted >= 1
        },
        streak3: {
            id: 'streak3',
            name: 'Racha de 3',
            description: 'Jugaste 3 días seguidos',
            icon: '🔥',
            condition: (progress) => progress.streak >= 3
        },
        streak7: {
            id: 'streak7',
            name: 'Racha de 7',
            description: 'Jugaste 7 días seguidos',
            icon: '⚡',
            condition: (progress) => progress.streak >= 7
        },
        explorer: {
            id: 'explorer',
            name: 'Explorador',
            description: 'Probaste 3 contextos diferentes',
            icon: '🗺️',
            condition: (progress) => (progress?.unlockedContexts || []).length >= 3
        },
        sumaMaster: {
            id: 'sumaMaster',
            name: 'Maestro de Sumas',
            description: 'Resolviste 10 problemas de suma',
            icon: '➕',
            condition: (progress) => (progress?.stats?.suma?.correct || 0) >= 10
        },
        restaMaster: {
            id: 'restaMaster',
            name: 'Maestro de Restas',
            description: 'Resolviste 10 problemas de resta',
            icon: '➖',
            condition: (progress) => (progress?.stats?.resta?.correct || 0) >= 10
        },
        perfectionist: {
            id: 'perfectionist',
            name: 'Perfeccionista',
            description: 'Completaste 5 problemas sin fallos',
            icon: '💎',
            condition(progress) {
                // This badge needs special tracking, so we'll check a dedicated counter
                return (progress.perfectProblems || 0) >= 5;
            }
        },
        level5: {
            id: 'level5',
            name: 'Detective Experto',
            description: 'Alcanzaste el nivel 5',
            icon: '🕵️',
            condition: (progress) => {
                if (!window.XP_SYSTEM) return false;
                return XP_SYSTEM.getLevelInfo(progress.xp).level >= 5;
            }
        },
        level10: {
            id: 'level10',
            name: 'Sherlock Holmes Jr.',
            description: 'Alcanzaste el nivel máximo',
            icon: '👑',
            condition: (progress) => {
                if (!window.XP_SYSTEM) return false;
                return XP_SYSTEM.getLevelInfo(progress.xp).level >= 10;
            }
        },
        starCollector: {
            id: 'starCollector',
            name: 'Coleccionista de Estrellas',
            description: 'Ganaste 25 estrellas',
            icon: '⭐',
            condition: (progress) => progress.stars >= 25
        },
        speedRunner: {
            id: 'speedRunner',
            name: 'Detective Veloz',
            description: 'Completaste un problema en menos de 2 minutos',
            icon: '⚡',
            condition: (progress) => progress.fastestProblem && progress.fastestProblem < 120
        },
        dedicated: {
            id: 'dedicated',
            name: 'Dedicado',
            description: 'Completaste 50 problemas',
            icon: '🎯',
            condition: (progress) => progress.problemsCompleted >= 50
        }
    },

    /**
     * Check all badges and unlock any that meet conditions
     */
    checkBadges() {
        if (!window.GamificationService) return;

        const progress = window.GamificationService.getProgress();
        const unlockedBadges = progress.badges || [];
        let newBadges = [];

        for (const [id, badge] of Object.entries(this.badges)) {
            // Skip if already unlocked
            if (unlockedBadges.includes(id)) continue;

            // Check condition
            if (badge.condition(progress)) {
                this.unlockBadge(badge);
                newBadges.push(id);
                unlockedBadges.push(id);
            }
        }

        if (newBadges.length > 0) {
            progress.badges = unlockedBadges;
            window.GamificationService.saveProgress();
        }
    },

    /**
     * Unlock a specific badge
     */
    unlockBadge(badge) {
        console.log(`🏅 Badge unlocked: ${badge.name}`);

        // Play confetti sound
        if (window.SoundService) {
            SoundService.playConfetti();
        }

        // Show badge modal
        this.showBadgeModal(badge);

        // Trigger confetti
        if (window.confetti) {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 }
            });
        }
    },

    /**
     * Show badge unlock modal
     */
    showBadgeModal(badge) {
        const modal = document.createElement('div');
        modal.className = 'badge-modal';
        modal.innerHTML = `
            <div class="badge-content">
                <div class="badge-icon-large">${badge.icon}</div>
                <h2>¡LOGRO DESBLOQUEADO!</h2>
                <h3>${badge.name}</h3>
                <p>${badge.description}</p>
                <button class="btn btn-primary" onclick="this.closest('.badge-modal').remove()">
                    ¡Genial!
                </button>
            </div>
        `;

        document.body.appendChild(modal);

        setTimeout(() => modal.classList.add('active'), 10);

        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (modal.parentElement) {
                modal.remove();
            }
        }, 5000);
    },

    /**
     * Get all unlocked badges for display
     */
    getUnlockedBadges() {
        if (!window.GamificationService) return [];

        const progress = window.GamificationService.getProgress();
        const unlockedIds = progress.badges || [];

        return unlockedIds.map(id => this.badges[id]).filter(Boolean);
    },

    /**
     * Get all badges (for displaying in achievements screen)
     */
    getAllBadges() {
        if (!window.GamificationService) return [];

        const progress = window.GamificationService.getProgress();
        const unlockedIds = progress.badges || [];

        return Object.values(this.badges).map(badge => ({
            ...badge,
            unlocked: unlockedIds.includes(badge.id)
        }));
    }
};

// Expose globally
window.BadgeSystem = BadgeSystem;

console.log('🏅 badgeSystem.js loaded');
